import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { UPDATE_STUDENT } from '../../actions/action_parent';

const Wrapper = styled.div`
  margin-top: 0;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  padding: 24px 24px 24px;
  margin-bottom: 32px;
`;

const MainHeader = styled.h4`
  margin: 0;
  font-size: 24px;
`;

const Divider = styled.hr`
  margin: 12px 0 20px 0;
  border-top: 1px solid #ddd;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  font-weight: bold;
  display: block;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
`;

const BackLink = styled(Link)`
  color: #479de6;
  text-decoration: none;
  margin-bottom: 16px;
  display: inline-block;

  &:hover {
    text-decoration: underline;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  margin-bottom: 16px;
  padding: 10px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
`;

const SkeletonSelect = styled.div`
  width: 100%;
  height: 42px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ParentEditStudent = ({ history, match }) => {
  const { id } = match.params;
  const dispatch = useDispatch();
  const parent = useSelector((state) => state.parent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showRelationship, setShowRelationship] = useState(false);
  const [relationshipOptions, setRelationshipOptions] = useState([]);
  const [relationshipLoading, setRelationshipLoading] = useState(true);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    guardianRelationship: '',
  });

  useEffect(() => {
    // Fetch relationship options from API
    const fetchRelationshipOptions = async () => {
      try {
        const response = await axios.get(
          '/api/v2/students/guardian_relationship_options',
          {
            headers: { publicToken: parent.publicToken },
          },
        );

        if (response.data.showRelationship) {
          setShowRelationship(true);
          // Convert API options to dropdown format
          const options = [
            { value: '', label: '- Select Relationship -' },
            ...Object.entries(response.data.options).map(([value, label]) => ({
              value,
              label,
            })),
          ];
          setRelationshipOptions(options);
        }
      } catch (error) {
        console.error('Failed to fetch relationship options:', error);
      } finally {
        setRelationshipLoading(false);
      }
    };

    fetchRelationshipOptions();

    // Find student from parent's students array
    const foundStudent = parent.students.find((s) => s.id === parseInt(id));
    if (foundStudent) {
      setFormData({
        firstName: foundStudent.firstName,
        lastName: foundStudent.lastName,
        guardianRelationship: foundStudent.guardianRelationship || '',
      });
      setLoading(false);
    } else {
      // If not found in Redux state, fetch from API
      fetchStudent();
    }
  }, [id, parent.students, parent.publicToken]);

  const fetchStudent = async () => {
    try {
      const response = await axios.get(`/api/v2/students/${id}`);
      const student = response.data.student;
      setFormData({
        firstName: student.firstName,
        lastName: student.lastName,
        guardianRelationship: student.guardianRelationship || '',
      });
    } catch (error) {
      console.error('Error fetching student:', error);
      setError('Failed to load student information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const updateData = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
      };

      // Only include guardian_relationship if shown and not empty
      if (showRelationship && formData.guardianRelationship) {
        updateData.guardian_relationship = formData.guardianRelationship;
      }

      const response = await axios.patch(`/api/v2/students/${id}`, updateData);

      // Update Redux state
      dispatch({
        type: UPDATE_STUDENT,
        payload: {
          id: parseInt(id),
          firstName: response.data.student.first_name,
          lastName: response.data.student.last_name,
          guardianRelationship: response.data.student.guardian_relationship,
          relationshipDisplay: response.data.student.relationshipDisplay,
        },
      });

      history.push(`/my-account/students/${id}`);
    } catch (error) {
      console.error('Error updating student:', error);
      setError('Failed to update student information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="bnl-dashboard-container">
      <div className="bnl-dashboard">
        <BackLink to={`/my-account/students/${id}`}>← Back to Student</BackLink>

        <MainHeader>Edit Student</MainHeader>
        <Divider />

        <Wrapper>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            {(showRelationship || relationshipLoading) && (
              <FormGroup>
                <Label htmlFor="guardianRelationship">
                  Your Relationship to Student
                </Label>
                {relationshipLoading ? (
                  <SkeletonSelect />
                ) : (
                  <Select
                    id="guardianRelationship"
                    name="guardianRelationship"
                    value={formData.guardianRelationship}
                    onChange={handleInputChange}
                  >
                    {relationshipOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                )}
              </FormGroup>
            )}

            <ButtonGroup>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <Link
                to={`/my-account/students/${id}`}
                className="btn btn-default"
              >
                Cancel
              </Link>
            </ButtonGroup>
          </form>
        </Wrapper>
      </div>
    </div>
  );
};

export default withRouter(ParentEditStudent);
